import { useCallback, useState } from 'react';
import { useChainId, useSignTypedData } from 'wagmi';

import { broadcastTradingSession } from '@/features/tradingSession/broadcast';
// the ceremony module, not the trading-session barrel: the barrel's components
// import this feature back.
import { completeTradingSessionCeremony } from '@/features/tradingSession/sessionCeremony';
import { useTradingSessionStore } from '@/features/tradingSession/store';
import { ensureTypedDataChain, useWallet } from '@/features/wallet';
import { useWalletStore } from '@/features/wallet/store';
import { describeErrorText } from '@/lib/errors';

/**
 * The window "Continue to funding" opens for a wallet-first user. Chosen for
 * them: the step never mentions trading windows, it only pops the signature.
 */
export const ONBOARDING_TRADING_WINDOW_MINUTES = 30;

export interface OnboardingTradingWindow {
    /**
     * Signs a default-length trading window with the connected wallet and
     * installs it exactly like the trading-session dialog would. Resolves true
     * once real mode is live, false when the user declined or the ceremony
     * failed; the failure text is left in `error` for the step to show.
     */
    openTradingWindow: () => Promise<boolean>;
    isOpening: boolean;
    error: string | null;
}

/**
 * A wallet-first user leaves the provision step with both wallets on chain and
 * nothing granting trading authority, so "Deposit now" used to open the
 * trading-window dialog over them. The signature is the same; this hook just
 * asks for it on the way to the funding step, with the window already chosen.
 */
export function useOnboardingTradingWindow(): OnboardingTradingWindow {
    const { address } = useWallet();
    // during wallet-first onboarding the wallet sits on the anchor the user
    // chose, so the connected chain keeps the auto-resolved session on it
    const connectedChainId = useChainId();
    const { signTypedDataAsync } = useSignTypedData();
    const [isOpening, setIsOpening] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const openTradingWindow = useCallback(async (): Promise<boolean> => {
        setError(null);
        if (!address) {
            setError('Connect your wallet to continue.');
            return false;
        }
        setIsOpening(true);
        try {
            const completed = await completeTradingSessionCeremony({
                method: 'eip712',
                durationMinutes: ONBOARDING_TRADING_WINDOW_MINUTES,
                preferredAnchorChainId: connectedChainId,
                wallet: address,
                signTypedData: async (typedData) => {
                    await ensureTypedDataChain(typedData);
                    return signTypedDataAsync(
                        typedData as Parameters<typeof signTypedDataAsync>[0],
                    );
                },
            });
            useTradingSessionStore.getState().applyStatus(completed.status);
            useWalletStore
                .getState()
                .saveAppSession(
                    completed.appToken.token,
                    completed.appToken.expiresAtMs,
                    completed.appToken.owner && completed.appToken.sessionId
                        ? {
                            owner: completed.appToken.owner,
                            sessionId: completed.appToken.sessionId,
                        }
                        : undefined,
                );
            broadcastTradingSession({ type: 'changed' });
            return true;
        } catch (caught) {
            setError(describeErrorText(caught, { operation: 'Signature' }));
            return false;
        } finally {
            setIsOpening(false);
        }
    }, [address, connectedChainId, signTypedDataAsync]);

    return { openTradingWindow, isOpening, error };
}
